import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { User } from '~/users/types';
import type { RequestPagination } from '~/common/types';
import type { TrackingDetails } from '~/dashboard/types';
import {
  LocationTypeEnum,
  RiskStateEnum,
  LocationSourceEnum,
} from '~/tracking/types';
import moment from 'moment';
import { UsersRequest } from '~/users/api/requests';

export class TrackingDetailsService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async fetch(pagination: RequestPagination, filter: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new UsersRequest({
      token: accessToken,
      params: { ...pagination, ...filter },
    });

    const { data, meta } = await this.apiClient.request(request);

    return {
      data: this.transformToTableData(data),
      meta: meta,
    };
  }

  transformToTableData(usersData: User[]) {
    const users = usersData.map(
      ({ userID, fullName, registrationStatus, locations }) => {
        let loneWorkerLogins = 0;
        let journeyManagementLogins = 0;

        let trackingCount = 0;
        let timeTrackingStarted = '';
        let timeTrackingEnded = '';
        let timeTrackingDurationInHours = 0;
        let totalTrackedHours = 0;

        let redOccurrence = 0;
        let timeRedStarted = '';
        let timeRedEnded = '';
        let timeRedDurationInSeconds = 0;
        let totalTimeInRed = 0;

        locations?.data.forEach((location) => {
          // Count lone worker and journey management logins
          if (
            location.type === LocationTypeEnum.LOGIN &&
            location.source === LocationSourceEnum.LONE_WORKER
          ) {
            loneWorkerLogins++;
          }
          if (
            location.type === LocationTypeEnum.LOGIN &&
            location.source === LocationSourceEnum.JOURNEY
          ) {
            journeyManagementLogins++;
          }

          // Tracking time for status changes
          if (
            timeTrackingStarted === '' &&
            location.type === LocationTypeEnum.LOGIN
          ) {
            trackingCount++;
            timeTrackingStarted = location.created_at;
          } else if (
            timeTrackingStarted !== '' &&
            location.type === LocationTypeEnum.LOGOUT
          ) {
            timeTrackingEnded = location.created_at;

            timeTrackingDurationInHours = moment
              .duration(
                moment(timeTrackingEnded).diff(moment(timeTrackingStarted)),
              )
              .asHours();

            totalTrackedHours += timeTrackingDurationInHours;
            timeTrackingStarted = '';
          }

          // Calculate red state occurrences and duration
          if (
            location.status === RiskStateEnum.RED &&
            location.type !== LocationTypeEnum.LOCATION_UPDATE
          ) {
            if (!timeRedStarted) {
              redOccurrence++;
              timeRedStarted = location.created_at;
            }
          } else if (
            timeRedStarted &&
            location.status !== RiskStateEnum.RED &&
            location.type !== LocationTypeEnum.LOCATION_UPDATE
          ) {
            // Handle transition out of RED status
            timeRedEnded = location.created_at;

            timeRedDurationInSeconds = moment
              .duration(moment(timeRedEnded).diff(moment(timeRedStarted)))
              .asSeconds();

            totalTimeInRed += timeRedDurationInSeconds;
            timeRedStarted = ''; // Reset the red timer
          }
        });

        const avgTrackedHours =
          trackingCount > 0
            ? (totalTrackedHours / trackingCount).toFixed(2)
            : '0';

        const avgTimeInRed =
          redOccurrence > 0 ? (totalTimeInRed / redOccurrence).toFixed(0) : '0';

        return {
          userID,
          name: fullName,
          registrationStatus,
          loneWorkerLogins,
          journeyManagementLogins,
          avgTrackedHours: `${avgTrackedHours} hrs`,
          redOccurrence,
          avgTimeInRed: `${avgTimeInRed} sec`,
        } as TrackingDetails;
      },
    );

    return users;
  }
}
